import React, { forwardRef, useId } from 'react'
import { cls } from '../../utils/helpers.js'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode
  error?: React.ReactNode
  hint?: React.ReactNode
  className?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, error, hint, className, onFocus, ...props }, ref) {
  const autoId = useId()
  const inputId = props.id || autoId
  const msgId = `${inputId}-msg`
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Auto-select all text on focus for number inputs so the default 0 gets replaced on typing
    if (props.type === 'number' || e.target.type === 'number') {
      e.target.select()
    }
    onFocus?.(e)
  }
  return (
    <div className={cls('field', className)}>
      {label && <label className="field-label" htmlFor={inputId}>{label}</label>}
      <input
        ref={ref}
        id={inputId}
        className={cls('field-input', error && 'field-error')}
        onFocus={handleFocus}
        aria-invalid={error ? 'true' : undefined}
        aria-required={props.required ? 'true' : undefined}
        aria-describedby={(error || hint) ? msgId : undefined}
        {...props}
      />
      {error && <span id={msgId} className="field-msg error" role="alert">{error}</span>}
      {hint && !error && <span id={msgId} className="field-msg hint">{hint}</span>}
    </div>
  )
})

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: React.ReactNode
  error?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ label, error, children, className, ...props }, ref) {
  const autoId = useId()
  const selectId = props.id || autoId
  const msgId = `${selectId}-msg`
  return (
    <div className={cls('field', className)}>
      {label && <label className="field-label" htmlFor={selectId}>{label}</label>}
      <select
        ref={ref}
        id={selectId}
        className={cls('field-input field-select', error && 'field-error')}
        aria-invalid={error ? 'true' : undefined}
        aria-required={props.required ? 'true' : undefined}
        aria-describedby={error ? msgId : undefined}
        {...props}
      >
        {children}
      </select>
      {error && <span id={msgId} className="field-msg error" role="alert">{error}</span>}
    </div>
  )
})

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode
  error?: React.ReactNode
  className?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ label, error, className, ...props }, ref) {
  const autoId = useId()
  const textareaId = props.id || autoId
  const msgId = `${textareaId}-msg`
  return (
    <div className={cls('field', className)}>
      {label && <label className="field-label" htmlFor={textareaId}>{label}</label>}
      <textarea
        ref={ref}
        id={textareaId}
        className={cls('field-input field-textarea', error && 'field-error')}
        aria-invalid={error ? 'true' : undefined}
        aria-required={props.required ? 'true' : undefined}
        aria-describedby={error ? msgId : undefined}
        {...props}
      />
      {error && <span id={msgId} className="field-msg error" role="alert">{error}</span>}
    </div>
  )
})
